import type { TaxFilingsGateway } from './TaxFilingsGateway';
import type {
  AnnualFormResponse,
  QuarterlyFormResponse,
  TaxFilingDashboardCardResponse,
  TaxFilingDashboardStatus,
  TaxFilingPlatformSummaryResponse,
  TaxFilingResponse,
} from './types';

const MOCK_LIABILITY = { id: 'mock-liability-1', formCode: 1, taxYear: 2026, quarter: 1 };
const MOCK_HOME_COMPANY_ID = 'mock-company';

/** Deliberately NOT the same email as the default mock-logged-in user (`admin@epn.local`, see
 * MockIdentityGateway) — the real backend requires a filing's preparer and approver to be different users,
 * and a mock that hardcoded the same email as whoever is logged in would make Approve/Deny permanently
 * untestable through this fixture (the UI's own preparer-check would always hide those buttons). */
const MOCK_PREPARER_EMAIL = 'other-preparer@epn.local';

const PERIODS: { formCode: number; quarter: number | null }[] = [
  { formCode: 1, quarter: 1 }, { formCode: 1, quarter: 2 }, { formCode: 1, quarter: 3 }, { formCode: 1, quarter: 4 },
  { formCode: 2, quarter: null },
];

function computeDueDate(formCode: number, taxYear: number, quarter: number | null): Date {
  let due: Date;
  if (formCode === 1) {
    due = quarter === 1 ? new Date(Date.UTC(taxYear, 3, 30))
      : quarter === 2 ? new Date(Date.UTC(taxYear, 6, 31))
      : quarter === 3 ? new Date(Date.UTC(taxYear, 9, 31))
      : new Date(Date.UTC(taxYear + 1, 0, 31));
  } else {
    due = new Date(Date.UTC(taxYear + 1, 0, 31));
  }
  const day = due.getUTCDay();
  if (day === 6) due.setUTCDate(due.getUTCDate() + 2);
  else if (day === 0) due.setUTCDate(due.getUTCDate() + 1);
  return due;
}

/** Local-development-only in-memory stand-in — the production build (VITE_USE_MOCKS=false, the default
 * in .env) always uses HttpTaxFilingsGateway against the real EPN.TaxCompliance.Api / EPN.TaxForms.Api. */
export class MockTaxFilingsGateway implements TaxFilingsGateway {
  private filings: TaxFilingResponse[] = [];
  private quarterlyForms: QuarterlyFormResponse[] = [];
  private annualForms: AnnualFormResponse[] = [];
  private liabilitiesById = new Map<string, { formCode: number; taxYear: number; quarter: number | null }>();

  reset(): void {
    this.filings = [];
    this.quarterlyForms = [];
    this.annualForms = [];
    this.liabilitiesById = new Map();
  }

  async listLiabilities() { return [MOCK_LIABILITY]; }

  async calculateLiability(input: { formCode: number; taxYear: number; quarter: number | null }) {
    const id = `mock-liability-${this.liabilitiesById.size + 1}`;
    this.liabilitiesById.set(id, input);
    return { id, ...input };
  }

  async listFilings(): Promise<TaxFilingResponse[]> { return this.filings; }

  async submitFiling(input: { formCode: number; taxYear: number; quarter: number | null }): Promise<TaxFilingResponse> {
    const filing: TaxFilingResponse = {
      id: `mock-filing-${this.filings.length + 1}`, companyId: MOCK_HOME_COMPANY_ID, formCode: input.formCode,
      taxYear: input.taxYear, quarter: input.quarter, status: 'PendingApproval', providerSubmissionId: null,
      failureReason: null, preparedByUserId: 'mock-preparer', preparedByEmailSnapshot: MOCK_PREPARER_EMAIL,
      preparedAtUtc: new Date().toISOString(), reviewedByUserId: null, reviewedByEmailSnapshot: null,
      reviewedAtUtc: null, denialReason: null, acceptedAtUtc: null, rejectedAtUtc: null,
    };
    this.filings.push(filing);
    return filing;
  }

  /** TEST-ONLY — the mock has no real multi-tenant session, so `submitFiling` always writes to the one
   * logged-in "home" company. EPN-40's SuperAdmin cross-company drill-in/summary views need at least one
   * OTHER company's filings to demonstrate against; this seeds one directly, mirroring how a backend test
   * fixture seeds a second company straight into the DbContext. Never called by any product runtime path. */
  seedFilingForAnotherCompanyForTests(companyId: string, input: { formCode: number; taxYear: number; quarter: number | null; status?: 'PendingApproval' | 'Accepted' | 'Rejected' | 'Denied' }): void {
    this.filings.push({
      id: `mock-filing-${this.filings.length + 1}`, companyId, formCode: input.formCode,
      taxYear: input.taxYear, quarter: input.quarter, status: input.status ?? 'PendingApproval', providerSubmissionId: null,
      failureReason: null, preparedByUserId: 'mock-preparer', preparedByEmailSnapshot: MOCK_PREPARER_EMAIL,
      preparedAtUtc: new Date().toISOString(), reviewedByUserId: null, reviewedByEmailSnapshot: null,
      reviewedAtUtc: null, denialReason: null, acceptedAtUtc: null, rejectedAtUtc: null,
    });
  }

  async approveFiling(id: string): Promise<TaxFilingResponse> {
    const filing = this.filings.find((f) => f.id === id)!;
    filing.status = 'Accepted';
    filing.providerSubmissionId = 'mock-submission-1';
    filing.reviewedByUserId = 'mock-approver';
    filing.reviewedByEmailSnapshot = 'admin@epn.local';
    filing.reviewedAtUtc = new Date().toISOString();
    filing.acceptedAtUtc = new Date().toISOString();
    return filing;
  }

  async denyFiling(id: string, reason: string): Promise<TaxFilingResponse> {
    const filing = this.filings.find((f) => f.id === id)!;
    filing.status = 'Denied';
    filing.denialReason = reason;
    filing.reviewedByUserId = 'mock-approver';
    filing.reviewedByEmailSnapshot = 'admin@epn.local';
    filing.reviewedAtUtc = new Date().toISOString();
    return filing;
  }

  async listQuarterlyForms(): Promise<QuarterlyFormResponse[]> { return this.quarterlyForms; }

  async generateQuarterlyForm(taxLiabilityId: string, taxFilingId: string | null): Promise<QuarterlyFormResponse> {
    const liability = this.liabilitiesById.get(taxLiabilityId);
    const form: QuarterlyFormResponse = {
      id: `mock-qform-${this.quarterlyForms.length + 1}`, companyId: 'mock-company',
      taxYear: liability?.taxYear ?? 2026, quarter: liability?.quarter ?? 1,
      taxLiabilityId, numberOfEmployees: 2, totalWages: 10000, totalTaxWithheld: 1250.5, totalDeposits: 0,
      balanceDue: 1250.5, taxFilingId, generatedByEmailSnapshot: 'admin@epn.local', generatedAtUtc: new Date().toISOString(),
    };
    this.quarterlyForms.push(form);
    return form;
  }

  async listAnnualForms(): Promise<AnnualFormResponse[]> { return this.annualForms; }

  async generateAnnualForm(taxLiabilityId: string, taxFilingId: string | null): Promise<AnnualFormResponse> {
    const liability = this.liabilitiesById.get(taxLiabilityId);
    const form: AnnualFormResponse = {
      id: `mock-aform-${this.annualForms.length + 1}`, companyId: 'mock-company', taxYear: liability?.taxYear ?? 2026,
      taxLiabilityId, numberOfEmployees: 2, totalWages: 10000, totalTaxWithheld: 420, totalDeposits: 0,
      balanceDue: 420, taxFilingId, generatedByEmailSnapshot: 'admin@epn.local', generatedAtUtc: new Date().toISOString(),
    };
    this.annualForms.push(form);
    return form;
  }

  async listDashboard(taxYear: number, companyId?: string): Promise<TaxFilingDashboardCardResponse[]> {
    const effectiveCompanyId = companyId ?? MOCK_HOME_COMPANY_ID;
    const companyFilings = this.filings.filter((f) => f.companyId === effectiveCompanyId);
    return this.buildCards(effectiveCompanyId, taxYear, companyFilings);
  }

  async listPlatformSummary(taxYear: number): Promise<TaxFilingPlatformSummaryResponse[]> {
    const companyIds = [...new Set(this.filings.filter((f) => f.taxYear === taxYear).map((f) => f.companyId))];
    return companyIds.map((companyId) => {
      const cards = this.buildCards(companyId, taxYear, this.filings.filter((f) => f.companyId === companyId));
      return {
        companyId, taxYear,
        notPreparedCount: cards.filter((c) => c.status === 'NotPrepared').length,
        inReviewCount: cards.filter((c) => c.status === 'InReview').length,
        filedCount: cards.filter((c) => c.status === 'Filed').length,
        needsAttentionCount: cards.filter((c) => c.status === 'NeedsAttention').length,
      };
    });
  }

  private buildCards(companyId: string, taxYear: number, companyFilings: TaxFilingResponse[]): TaxFilingDashboardCardResponse[] {
    return PERIODS.map((p) => {
      const filing = companyFilings.find((f) => f.formCode === p.formCode && f.taxYear === taxYear && f.quarter === p.quarter);
      const dueDate = computeDueDate(p.formCode, taxYear, p.quarter);
      let status: TaxFilingDashboardStatus;
      if (!filing) status = 'NotPrepared';
      else if (filing.status === 'PendingApproval') status = 'InReview';
      else if (filing.status === 'Accepted') status = 'Filed';
      else status = 'NeedsAttention';

      return {
        companyId, formCode: p.formCode, taxYear, quarter: p.quarter, dueDate: dueDate.toISOString().slice(0, 10), status,
        taxFilingId: filing?.id ?? null, providerSubmissionId: filing?.providerSubmissionId ?? null,
        failureReason: filing?.failureReason ?? null,
        preparedByEmailSnapshot: filing?.preparedByEmailSnapshot ?? null, preparedAtUtc: filing?.preparedAtUtc ?? null,
        reviewedByEmailSnapshot: filing?.reviewedByEmailSnapshot ?? null, reviewedAtUtc: filing?.reviewedAtUtc ?? null,
        denialReason: filing?.denialReason ?? null,
      };
    });
  }
}
